import {
    PlatformActions,
    PlatformId,
    PlatformAction,
    ShopifyLinkArgs,
    LinkArgs
} from "../views/page-content/platforms/types";
import {
    getTwitchIngestServers,
    getTwitchUserAndChannel
} from "store/platforms/thunks";
import { useCallback } from "react";
import { client } from "api/client";
import { client as clientCore } from "api/client-core";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/reducers";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import store, { AppDispatch } from "store/store";
import { facebook as fb } from "api/facebook/facebook-client";
import { youtube as yt } from "api/youtube/youtube-client";
import rollbar from "helpers/rollbar";
import { StreamingProvider } from "@switcherstudio/switcher-api-client";
import { useClaimCheck } from "hooks/useClaimCheck";
import { useNavigate, useLocation } from "react-router-dom";
import { versionGreaterOrEqual } from "helpers/strings";
import { useSwitcherSdk } from "./useSwitcherSdk";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { AttentionModal } from "components/modal/AttentionModal";
import { useTranslation } from "react-i18next";
import {
    INSTAGRAM_SCOPES,
    buildFacebookAuthUrl,
    getFacebookScopes
} from "helpers/facebookAuth";
import { isInstagramEnabled } from "helpers/instagram";

const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI;

export function useAllPlatformActions() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((s: RootState) => s.user);
    const isVLH = useClaimCheck("vlh");
    const isLegacySimulcast = useClaimCheck("legacysimulcast");
    const onlySimulcastManage = isVLH && !isLegacySimulcast;

    // Connecting platforms as part of a multistream will
    // have different routing afterwards
    const { pathname } = useLocation();
    const { isInBrowser, switcherSdk } = useSwitcherSdk();
    const isSimulcastRoute = pathname.includes("simulcast");

    async function deleteStreamSettings(platformId: string) {
        try {
            await client.externalProviderSwitcherStreamSettings_DeleteExternalProviderSwitcherStreamSetting(
                platformId
            );
        } catch (err) {
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: "errors:disconnect-account-error"
                })
            );
        }
    }

    async function getPlatform(platformId: string): Promise<StreamingProvider> {
        const response = await client.streamingProviders_GetStreamingProvider(
            platformId
        );

        // Workaround -- GetStreamingProvider (above) falls through to
        // GetStreamingProviders if the platform id is not a guid, and
        // the platform does not have its own dedicated controller action
        // (churchstreamingtv, streamspot). Call GetStreamingProviders again
        // with browse = true, and pick out the provider we want.
        if ((response as any).StreamingProviders?.length) {
            const res = await client.streamingProviders_GetStreamingProviders(
                true
            );
            const platform = res.StreamingProviders.find(
                (s) => s.Id === platformId
            );
            return platform;
        } else {
            return response;
        }
    }

    /* Return true if app version uses the native component to create the events, false if app version uses the webview */
    const switcherNativeEventCreation = versionGreaterOrEqual(
        String(switcherSdk.appVersion),
        11,
        5
    );

    async function fetchInstagramEnabled(): Promise<boolean> {
        try {
            const res = await client.streamingProviders_GetStreamingProviders();
            return isInstagramEnabled(res.StreamingProviders);
        } catch (e) {
            return false;
        }
    }

    async function linkWithFacebookLogin({
        providerId,
        scopes,
        configId,
        finalUrl,
        args
    }: {
        providerId: string;
        scopes: string[];
        configId?: string;
        finalUrl: string;
        args?: LinkArgs;
    }) {
        const userToken = await client.account_GetUserToken();
        const isolatedReturnUrl =
            switcherSdk.api.versionMajor >= 2
                ? switcherSdk.getIsolatedWebViewReturnUrl()
                : null;

        const state = {
            providerId,
            userId: switcherSdk.auth.userId || user.ticket?.userId,
            userToken: userToken,
            finalUrl: args?.finalReturnUrl ?? isolatedReturnUrl ?? finalUrl
        };

        /** If user is in app, force Facebook reauth so that users who wish to
         * disconnect one account and reconnect with a different one are able.
         * This is a workaround. Hopefully Facebook updates their auth flow at
         * some point. */
        const authURL = buildFacebookAuthUrl({
            state,
            clientId: import.meta.env.VITE_FACEBOOK_CLIENTID,
            redirectUri: REDIRECT_URI,
            scopes,
            configId,
            reauthenticate: !isInBrowser
        });

        if (switcherSdk.api.versionMajor >= 2) {
            switcherSdk.openIsolatedWebView(authURL.href, null, null, () => {
                switcherSdk.closeIsolatedWebView(null, () => {
                    if (switcherNativeEventCreation) {
                        switcherSdk.dismiss({
                            reason: "connected"
                        });
                    } else {
                        navigate(finalUrl);
                    }
                });
            });
        } else {
            window.location.replace(authURL.href);
        }
    }

    const unlinkFacebook = useCallback(async () => {
        try {
            await client.facebook_DeleteCredentials();
            const streamDestinations =
                await clientCore.streamDestinations_GetStreamDestinations_v2();

            const defaultDestination = streamDestinations.find(
                (sd) => sd.Details?.IsDefault
            );

            if (defaultDestination) {
                await clientCore.streamDestinations_DeleteAsync_v2(
                    defaultDestination?.Details?.Id
                );
            }
        } catch (e) {
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: "errors:disconnect-account-error"
                })
            );
        }
    }, [dispatch]);

    const actions: PlatformActions = {
        /** TODO: re-evaluate redirectUris/finalUris, especially for orgs.
         * For now, just use the /platforms path for the redirect.
         */
        [PlatformId.Facebook]: {
            async link(args?: LinkArgs) {
                const finalUrl = isSimulcastRoute
                    ? "/platforms/simulcast/destinations/facebook/add"
                    : onlySimulcastManage
                    ? switcherNativeEventCreation || isInBrowser
                        ? "/manage-platforms"
                        : "/platforms"
                    : "/platforms/facebook";

                await linkWithFacebookLogin({
                    providerId: "facebook",
                    scopes: getFacebookScopes(await fetchInstagramEnabled()),
                    finalUrl,
                    args
                });
            },
            unlink: unlinkFacebook,
            init: useCallback(async (destinationInfo) => {
                const { destinationId, destinationType } = destinationInfo;
                const {
                    platforms: { facebook }
                } = store.getState();
                const { selectedDestination } = facebook;
                const getExistingStreams = async () => {
                    // this needs to change soon.. need better defaults.
                    if (
                        destinationId === "-1" ||
                        selectedDestination.id === "-1"
                    ) {
                        return [];
                    }
                    const res = await fb.helpers.getScheduledStreamsByType(
                        destinationId || selectedDestination.id,
                        destinationType || selectedDestination.edge
                    );
                    if (res.hasOwnProperty("live_videos")) {
                        return res.live_videos.data.filter(
                            (v) => v.broadcast_start_time
                        );
                    } else {
                        return [];
                    }
                };
                const existingStreams = await getExistingStreams();
                const initialState = {
                    imgSrc: selectedDestination?.picture?.data?.url,
                    name: selectedDestination?.name,
                    existingStreams,
                    hasAccess: true
                };

                return initialState;
            }, []),
            getPlatform: useCallback(() => getPlatform(PlatformId.Facebook), [])
        },
        [PlatformId.Instagram]: {
            async link(args?: LinkArgs) {
                const finalUrl = onlySimulcastManage
                    ? switcherNativeEventCreation || isInBrowser
                        ? "/manage-platforms"
                        : "/platforms"
                    : "/platforms/instagram";

                await linkWithFacebookLogin({
                    providerId: "instagram",
                    scopes: INSTAGRAM_SCOPES,
                    configId: import.meta.env.VITE_INSTAGRAM_CONFIG_ID,
                    finalUrl,
                    args
                });
            },
            unlink: unlinkFacebook,
            getPlatform: useCallback(
                () => getPlatform(PlatformId.Instagram),
                []
            )
        },
        [PlatformId.Youtube]: {
            async link(args?: LinkArgs) {
                const userToken = await client.account_GetUserToken();
                const redirectUrl = encodeURIComponent(REDIRECT_URI); //TODO: url
                const isolatedReturnUrl =
                    switcherSdk.api.versionMajor >= 2
                        ? switcherSdk.getIsolatedWebViewReturnUrl()
                        : null;

                const finalUrl = isSimulcastRoute
                    ? "/platforms/simulcast/destinations/youtube/add"
                    : onlySimulcastManage
                    ? switcherNativeEventCreation || isInBrowser
                        ? "/manage-platforms"
                        : "/platforms"
                    : "/platforms/youtube";

                const state = {
                    providerId: "youtube",
                    userId: switcherSdk.auth.userId || user.ticket?.userId,
                    userToken: userToken,
                    finalUrl:
                        args?.finalReturnUrl ?? isolatedReturnUrl ?? finalUrl
                };

                const authURL =
                    "https://accounts.google.com/o/oauth2/auth" +
                    "?returnUrl=" +
                    encodeURIComponent(isolatedReturnUrl) +
                    "&response_type=code&client_id=" +
                    import.meta.env.VITE_YOUTUBE_CLIENT_ID +
                    "&redirect_uri=" +
                    redirectUrl +
                    "&state=" +
                    encodeURIComponent(JSON.stringify(state)) +
                    "&scope=" +
                    "https://www.googleapis.com/auth/youtube&access_type=offline&include_granted_scopes=true&approval_prompt=force";

                if (switcherSdk.api.versionMajor >= 2) {
                    switcherSdk.openIsolatedWebView(
                        authURL,
                        null,
                        null,
                        function () {
                            switcherSdk.closeIsolatedWebView(null, () => {
                                if (switcherNativeEventCreation) {
                                    switcherSdk.dismiss({
                                        reason: "connected"
                                    });
                                } else {
                                    navigate(finalUrl);
                                }
                            });
                        }
                    );
                } else {
                    window.location.replace(authURL);
                }
            },
            unlink: useCallback(async () => {
                try {
                    await client.youTube_DeleteCredentials();
                } catch (e) {
                    dispatch(
                        addNotification({
                            type: NotificationType.Danger,
                            message: "errors:disconnect-account-error"
                        })
                    );
                }
            }, [dispatch]),
            init: useCallback(async (destinationInfo) => {
                const { destinationId } = destinationInfo;
                const getExistingStreams = async () => {
                    const existingScheduled = await yt.liveBroadcasts.list({
                        maxResults: 500,
                        part: "id,snippet,contentDetails,status",
                        broadcastType: "event",
                        broadcastStatus: "upcoming"
                    });
                    return existingScheduled.items.filter(
                        (i) => !!i.snippet.scheduledStartTime
                    );
                };
                const listArgs = {
                    part: "id,snippet,contentDetails",
                    ...(destinationId ? { id: destinationId } : { mine: true })
                };
                const { items } = await yt.channels.list(listArgs);
                const channelInfo = items[0];

                return {
                    name: channelInfo?.snippet?.title,
                    imgSrc: channelInfo?.snippet?.thumbnails?.default?.url,
                    existingStreams: await getExistingStreams(),
                    hasAccess: true
                };
            }, []),
            getPlatform: useCallback(() => getPlatform(PlatformId.Youtube), [])
        },
        [PlatformId.Shopify]: {
            link: useCallback(
                async ({ finalReturnUrl }: ShopifyLinkArgs) => {
                    const userToken = await client.account_GetUserToken();
                    const isolatedReturnUrl =
                        switcherSdk.api.versionMajor >= 2
                            ? switcherSdk.getIsolatedWebViewReturnUrl()
                            : null;

                    const state = {
                        providerId: PlatformId.Shopify,
                        userId: switcherSdk.auth.userId || user.ticket?.userId,
                        userToken: userToken,
                        finalUrl:
                            isolatedReturnUrl ??
                            finalReturnUrl ??
                            `/platforms/${PlatformId.Shopify}`
                    };

                    const authParams = new URLSearchParams();
                    authParams.append(
                        "client_id",
                        import.meta.env.VITE_SHOPIFY_CLIENTID
                    );
                    const scopes = [
                        "unauthenticated_read_checkouts",
                        "unauthenticated_write_checkouts",
                        "unauthenticated_read_customers",
                        "unauthenticated_write_customers",
                        "unauthenticated_read_product_listings",
                        "unauthenticated_read_product_tags",
                        "unauthenticated_read_selling_plans",
                        "unauthenticated_read_product_inventory",
                        "read_orders",
                        "read_products"
                    ];
                    authParams.append("redirect_uri", REDIRECT_URI);
                    authParams.append("scope", scopes.join(","));
                    authParams.append("state", JSON.stringify(state));

                    const authUrl = `https://accounts.shopify.com/store-login?redirect=${encodeURIComponent(
                        `oauth/authorize?${authParams.toString()}`
                    )}`;

                    if (switcherSdk.api.versionMajor >= 2) {
                        switcherSdk.openIsolatedWebView(
                            authUrl,
                            null,
                            null,
                            () => {
                                switcherSdk.closeIsolatedWebView(null, () => {
                                    navigate(
                                        `/platforms/${PlatformId.Shopify}`
                                    );
                                });
                            }
                        );
                    } else {
                        window.location.replace(authUrl);
                    }
                },
                [navigate, user.ticket, switcherSdk]
            ),
            unlink: useCallback(async () => {
                try {
                    await client.shopify_DeleteCredentials();
                } catch (e) {
                    dispatch(
                        addNotification({
                            type: NotificationType.Danger,
                            message: "errors:disconnect-account-error"
                        })
                    );
                }
            }, [dispatch]),
            getPlatform: useCallback(async () => {
                let credentials = null;
                try {
                    credentials = await client.shopify_GetCredentials();
                } catch (e) {}

                return {
                    Id: "shopify",
                    LogoUrl:
                        "https://switcherstudio.blob.core.windows.net/staticcontent/images/providers/shopify.png",
                    IsLinked: credentials?.length > 0
                } as StreamingProvider;
            }, [])
        } as PlatformAction,
        [PlatformId.Twitch]: {
            async link(args?: LinkArgs) {
                const userToken = await client.account_GetUserToken();
                const isolatedReturnUrl =
                    switcherSdk.api.versionMajor >= 2
                        ? switcherSdk.getIsolatedWebViewReturnUrl()
                        : null;

                const finalUrl = isSimulcastRoute
                    ? "/platforms/simulcast/destinations/twitch/add"
                    : onlySimulcastManage
                    ? switcherNativeEventCreation || isInBrowser
                        ? "/manage-platforms"
                        : "/platforms"
                    : "/platforms/twitch";

                const state = {
                    providerId: "twitch",
                    userId: switcherSdk.auth.userId || user.ticket?.userId,
                    userToken: userToken,
                    finalUrl:
                        args?.finalReturnUrl ?? isolatedReturnUrl ?? finalUrl
                };
                const perms = encodeURIComponent(
                    [
                        "user_read",
                        "channel_read",
                        "channel_editor",
                        "user:read:email",
                        "channel:read:editors",
                        "channel:read:stream_key",
                        "channel:manage:broadcast",
                        "channel:manage:videos",
                        "channel:moderate",
                        "chat:read",
                        "chat:edit",
                        "whispers:read",
                        "whispers:edit",
                        "channel:read:subscriptions"
                    ].join(" ")
                );
                const authRedirect = encodeURIComponent(REDIRECT_URI);
                const authURL =
                    "https://id.twitch.tv/oauth2/authorize?" +
                    "client_id=" +
                    import.meta.env.VITE_TWITCH_CLIENT_ID +
                    "&redirect_uri=" +
                    authRedirect +
                    "&response_type=code" +
                    "&scope=" +
                    perms +
                    "&state=" +
                    encodeURIComponent(JSON.stringify(state));

                if (switcherSdk.api.versionMajor >= 2) {
                    switcherSdk.openIsolatedWebView(authURL, null, null, () => {
                        switcherSdk.closeIsolatedWebView(null, () => {
                            if (switcherNativeEventCreation) {
                                switcherSdk.dismiss({ reason: "connected" });
                            } else {
                                navigate(finalUrl);
                            }
                        });
                    });
                } else {
                    window.location.replace(authURL);
                }
            },
            unlink: useCallback(async () => {
                try {
                    await client.twitch_DeleteCredentials();
                } catch (e) {
                    dispatch(
                        addNotification({
                            type: NotificationType.Danger,
                            message: "errors:disconnect-account-error"
                        })
                    );
                }
            }, [dispatch]),
            init: useCallback(async () => {
                await dispatch(getTwitchIngestServers());
                const info = await dispatch(getTwitchUserAndChannel()).unwrap();

                return {
                    name: info.user.display_name,
                    imgSrc: info.user.profile_image_url,
                    hasAccess: true
                };
            }, [dispatch]),
            getPlatform: useCallback(() => getPlatform(PlatformId.Twitch), [])
        } as PlatformAction,
        [PlatformId.VideoPlayer]: {
            init: useCallback(async () => {
                return {
                    hasAccess: true
                };
            }, []),
            getPlatform: useCallback(
                () => getPlatform(PlatformId.VideoPlayer),
                []
            )
        } as PlatformAction,
        [PlatformId.StripeConnect]: {
            async link() {
                try {
                    window.location.replace(
                        (
                            await client.stripeConnect_GetAccountLink(
                                user.userInfo?.User?.Details?.Id,
                                REDIRECT_URI
                            )
                        ).url
                    );
                } catch (e) {
                    rollbar.error(
                        "Error getting stripe connect account link",
                        e
                    );
                }
            }
        },
        [PlatformId.MsStream]: {
            async unlink() {
                return deleteStreamSettings(PlatformId.MsStream);
            },
            getPlatform: useCallback(() => getPlatform(PlatformId.MsStream), [])
        },
        [PlatformId.Caast]: {
            async unlink() {
                return deleteStreamSettings(PlatformId.Caast);
            },
            getPlatform: useCallback(() => getPlatform(PlatformId.Caast), [])
        },
        [PlatformId.Instafeed]: {
            async unlink() {
                return deleteStreamSettings(PlatformId.Instafeed);
            },
            getPlatform: useCallback(
                () => getPlatform(PlatformId.Instafeed),
                []
            )
        },
        [PlatformId.AmazonLive]: {
            async unlink() {
                return deleteStreamSettings(PlatformId.AmazonLive);
            },
            getPlatform: useCallback(
                () => getPlatform(PlatformId.AmazonLive),
                []
            )
        },
        [PlatformId.AzureMediaServices]: {
            async unlink() {
                return deleteStreamSettings(PlatformId.AzureMediaServices);
            },
            getPlatform: () => getPlatform(PlatformId.AzureMediaServices)
        },
        [PlatformId.SwitchboardLive]: {
            async link() {
                try {
                    await client.switchboard_GenerateJWT();
                    navigate("/platforms/switchboard");
                } catch (e) {
                    rollbar.error("Error generating switchboard JWT", e);
                }
            },
            async unlink() {
                // delete credentials & remove stream settings.
                await client.switchboard_DeleteCredentials();
            },
            getPlatform: useCallback(
                () => getPlatform(PlatformId.SwitchboardLive),
                []
            )
        },
        [PlatformId.ChurchStreamingTv]: {
            async unlink() {
                return deleteStreamSettings(PlatformId.ChurchStreamingTv);
            },
            getPlatform: useCallback(
                () => getPlatform(PlatformId.ChurchStreamingTv),
                []
            )
        }
    };

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    // Disconnect account - after confirmation, will unlink and then update local data
    const disconnect = async (
        platformId: PlatformId,
        providers: StreamingProvider[],
        setProviders: (providers: StreamingProvider[]) => void
    ) => {
        dispatch(
            setActiveModal({
                id: Modals.AttentionModal,
                type: Modals.AttentionModal,
                component: (
                    <AttentionModal
                        isOpen={true}
                        setIsOpen={closeAttentionModal}
                        handleCancel={closeAttentionModal}
                        hasContinueButton={true}
                        handleContinue={async () => {
                            await actions[platformId]?.unlink();
                            setProviders(
                                providers.map((p) =>
                                    p.Id === platformId
                                        ? { ...p, IsLinked: false }
                                        : p
                                )
                            );
                        }}
                        continueText={t("buttons:confirm")}
                    >
                        <div>{t("platforms:confirm-account-disconnect")}</div>
                    </AttentionModal>
                )
            })
        );
    };

    // Connect account - after redirect to account login, will redirect to overrideReturnUrl if provided otherwise the determine finalUrl
    const connect = async (platformId: PlatformId, returnUrl?: string) => {
        await actions[platformId]?.link({ finalReturnUrl: returnUrl });
    };

    // Should the user be permitted to manage this platform, i.e. create broadcasts directly to it?
    const canManage = useCallback(
        (platformId: PlatformId): boolean =>
            !onlySimulcastManage || platformId === PlatformId.Simulcast,
        [onlySimulcastManage]
    );

    return { actions, disconnect, connect, canManage };
}

export function usePlatformActions(platformId: PlatformId) {
    const { actions } = useAllPlatformActions();

    return {
        link: actions[platformId]?.link,
        unlink: actions[platformId]?.unlink,
        init: actions[platformId]?.init,
        getPlatform: actions[platformId]?.getPlatform
    } as PlatformAction;
}
